// Design tokens for the PDF 2b standard (docs/pdf-style-guide.md) — the
// shared color/badge/font vocabulary every generator and js/pdf/style.js
// draw from. Pure data + pure functions: no imports, so it can be sanity
// tested with a bare `node -e` and never carries build-order risk.

// Global Constraints color table. RGB triplets, jsPDF's format for
// setTextColor/setDrawColor/setFillColor(...rgb).
export const T = {
  INK: [10, 11, 14],
  BODY: [52, 64, 84],
  SEC: [71, 84, 103],
  // 2026-09-08 (Renan): "muito clarinho, não dá para ler". The old greys
  // (#98A2B3 at 2.6:1, #7A7D87 at 4.2:1) failed on paper and on screen for
  // 7pt caps labels. Both now clear 4.5:1 on white; the hierarchy (MUTED2 a
  // step darker for meta-strip labels and row numbers) is kept.
  MUTED: [102, 112, 133],
  MUTED2: [95, 102, 115],
  HAIR: [228, 231, 236],
  HAIR_LT: [242, 244, 247],
  STRIP: [245, 246, 248],
  SIG_LINE: [208, 213, 221],
  // Narwal v3 brand orange (#F89010). Was the retired hazard yellow
  // (#FFC72C) until 2026-08-09 — the rebrand reached the app and the site
  // but not the documents, so every generated PDF still stamped the old
  // color on the one artifact the GC actually receives.
  ORDINEX: [248, 144, 16],
  DOT: [139, 141, 150],
  WHITE: [255, 255, 255],
};

// Severity badges (bg/fg/bd), flattened over white and kept literal so
// callers never recompute them and drift from the approved palette.
export const BADGE = {
  low: { bg: [228, 248, 236], fg: [21, 128, 61], bd: [167, 232, 191] },
  medium: { bg: [254, 241, 221], fg: [180, 83, 9], bd: [251, 211, 145] },
  high: { bg: [253, 233, 233], fg: [185, 28, 28], bd: [249, 180, 180] },
};

// Data-emphasis colors — values that live in a document's DATA (overtime
// hours, correction marks, the schedule's today line), never in its chrome.
// One shared data-red: the generators alias it to their domain names
// (OTRED / REDCL / TODAY_RED) instead of each carrying its own literal.
// GREEN is the app's --success (#2E7D5B): a production pace that rose.
export const DATA = { RED: [220, 38, 38], GREEN: [46, 125, 91] };

// jsPDF family names registered by js/vendor/plex-fonts.js (Task 1) — this
// is just the name contract, not the embed.
export const FONT = { sans: 'PlexSans', semi: 'PlexSansSemi', mono: 'PlexMono' };

// Per-channel alpha blend of fg over bg, rounded to an integer RGB triplet.
export function flatten(bgRgb, fgRgb, alpha) {
  return [0, 1, 2].map(i => Math.round(bgRgb[i] * (1 - alpha) + fgRgb[i] * alpha));
}

// WCAG relative luminance (0 = black, 1 = white).
export function luminance([r, g, b]) {
  const lin = (c) => {
    c /= 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

// Contrast-safe palette for drawing on an arbitrary tenant band color
// (letterhead strip, cover band, etc.) — every derived value stays legible
// no matter which primary color the tenant picked.
export function tenantScheme(bandRgb) {
  const light = luminance(bandRgb) > 0.5;
  const on = light ? T.INK : T.WHITE;
  return {
    band: bandRgb,
    on,
    onMuted: flatten(bandRgb, on, 0.55),
    wordmark: light ? on : T.ORDINEX,
    chipBg: flatten(bandRgb, on, 0.10),
    chipBd: flatten(bandRgb, on, 0.14),
    logoBg: flatten(bandRgb, on, light ? 0.08 : 0.10),
  };
}
